import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
} from "@/components/docs";
import { docsColors } from "@/lib/docs-theme";

export default function EmailContactsPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "Email Notifications" },
  ];

  return (
    <DocLayout
      title="Email Notifications - Uptime Monitor Documentation"
      description="Configure email alerts for monitor status changes. Learn about email credits, templates, and delivery options."
      canonical="https://uptime-monitor.io/docs/notifications/email"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Email Notifications"
        description="Email alerts sent when your monitors detect issues. Works with any email client or service."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
            Why Email Notifications?
          </h2>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Works with any email client or service
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Creates an email trail of notifications
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Easy forwarding and sharing with team members
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2">✓</span>
              Integration with email filters and rules
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Setting Up Email Contacts */}
      <DocSection title="Setting Up Email Contacts">
        <DocCard>
          <ol className="space-y-4">
            <li>
              <div className="flex items-start">
                <DocStepIndicator number={1} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                    Navigate to Contacts
                  </h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Go to Settings → Contacts in your dashboard
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <DocStepIndicator number={2} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                    Add Email Contact
                  </h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Click &quot;Add Contact&quot; and select &quot;Email&quot; as the type
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <DocStepIndicator number={3} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>Enter Details</h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Provide a name for the contact and the email address
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <DocStepIndicator number={4} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>Save Contact</h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Click save to add the email contact
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <DocStepIndicator number={5} />
                <div>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-1`}>
                    Assign to Monitors
                  </h3>
                  <p className={`${docsColors.text.body} text-sm`}>
                    Add the contact to any monitors that should send alerts to this email
                  </p>
                </div>
              </div>
            </li>
          </ol>

          <DocAlert variant="info" className="mt-6 p-4">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className={docsColors.text.heading}>Note:</strong> Emails are sent immediately
              to the addresses you provide. Ensure email addresses are correct before adding them.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Email Content */}
      <DocSection title="Email Content">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Email notifications are simple plain text messages with key information:
          </p>

          <div className="space-y-4">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Down Alert Format</h3>
              <DocCodeBlock className="text-sm">
                <div className={`font-mono ${docsColors.text.body}`}>
                  <div className="text-red-600 dark:text-red-400 font-semibold">
                    Subject: Production Server is DOWN!
                  </div>
                  <div className="mt-3 whitespace-pre-wrap">{`Production Server is DOWN!\nReason: Connection timeout after 10000ms.\n\nSee incident details at https://uptime-monitor.io/dashboard/monitors/[monitor-id]\n\n\n-----\nUptime Monitor`}</div>
                </div>
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Recovery Alert Format
              </h3>
              <DocCodeBlock className="text-sm">
                <div className={`font-mono ${docsColors.text.body}`}>
                  <div className="text-green-600 dark:text-green-400 font-semibold">
                    Subject: Production Server is UP!
                  </div>
                  <div className="mt-3 whitespace-pre-wrap">{`Production Server is UP! It was down for 5m 23s.\n\nSee incident details at https://uptime-monitor.io/dashboard/monitors/[monitor-id]\n\n\n-----\nUptime Monitor`}</div>
                </div>
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                SSL Certificate Warnings
              </h3>
              <DocCodeBlock className="text-sm">
                <div className={`font-mono ${docsColors.text.body}`}>
                  <div className="text-yellow-600 dark:text-yellow-400 font-semibold mb-2">
                    Alert messages by timing:
                  </div>
                  <div className="space-y-2">
                    <div>
                      •{" "}
                      <span className="text-red-600 dark:text-red-400">
                        &quot;example.com certificate has expired!&quot;
                      </span>
                    </div>
                    <div>
                      •{" "}
                      <span className="text-orange-600 dark:text-orange-400">
                        &quot;example.com certificate will expire tomorrow!&quot;
                      </span>
                    </div>
                    <div>
                      •{" "}
                      <span className="text-yellow-600 dark:text-yellow-400">
                        &quot;example.com certificate will expire in 7 days!&quot;
                      </span>
                    </div>
                    <div>
                      •{" "}
                      <span className={`${docsColors.text.muted}`}>
                        &quot;example.com certificate will expire in 14 days.&quot;
                      </span>
                    </div>
                    <div>
                      •{" "}
                      <span className={`${docsColors.text.muted}`}>
                        &quot;example.com certificate will expire in 30 days.&quot;
                      </span>
                    </div>
                  </div>
                </div>
              </DocCodeBlock>
            </div>
          </div>

          <DocAlert variant="info" className="mt-4 p-4">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className={docsColors.text.heading}>Note:</strong> Email notifications are
              sent as plain text for maximum compatibility. The message content is concise and
              includes a link to view full incident details in your dashboard.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Email Credits */}
      <DocSection title="Email Credits System">
        <DocCard>
          <div className="space-y-6">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                How Email Credits Work
              </h3>
              <p className={`${docsColors.text.body} mb-4`}>
                Email credits ensure reliable delivery through premium email services:
              </p>
              <ul className={`space-y-2 ${docsColors.text.body}`}>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  Each email notification consumes 1 credit
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  Credits reset daily at noon UTC
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  Unused daily credits don&apos;t roll over
                </li>
                <li className="flex items-start">
                  <span className={`${docsColors.text.muted} mr-2`}>•</span>
                  Both down and recovery alerts use credits
                </li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                Daily Email Limits
              </h3>
              <DocCodeBlock className="">
                <p className={`${docsColors.text.body}`}>
                  <strong className={docsColors.text.heading}>Daily limit:</strong> Matches your
                  plan&apos;s monitor count
                  <br />
                  <span className={`text-sm ${docsColors.text.muted}`}>
                    For example: 50 monitor plan = 50 emails per day
                  </span>
                </p>
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                Additional Email Packages
              </h3>
              <p className={`${docsColors.text.body} mb-3`}>
                Need more emails? Purchase additional credits that never expire:
              </p>
              <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
                <li>• Extra email packages available in Settings → Billing</li>
                <li>• Purchased credits are used after daily credits are exhausted</li>
                <li>• No expiration on purchased credits</li>
                <li>• Use across all monitors in your account</li>
              </ul>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Delivery and Reliability */}
      <DocSection title="Delivery & Reliability">
        <DocCard>
          <div className="space-y-4">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Delivery Speed</h3>
              <p className={`${docsColors.text.body}`}>
                Email notifications are sent immediately when status changes are detected. Most
                emails arrive within seconds, though delivery time can vary based on receiving email
                servers.
              </p>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Spam Prevention</h3>
              <p className={`${docsColors.text.body} mb-2`}>
                To ensure notifications reach your inbox:
              </p>
              <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-6`}>
                <li>• Add alert@m.uptime-monitor.io to your contacts</li>
                <li>• Check spam/junk folders initially</li>
                <li>• Mark notifications as &quot;Not Spam&quot; if filtered</li>
                <li>• Configure email rules to prioritize alerts</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                Email Service Provider
              </h3>
              <p className={`${docsColors.text.body}`}>
                We use enterprise-grade email delivery services with high deliverability rates,
                dedicated IPs, and proper authentication (SPF, DKIM, DMARC).
              </p>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Email Notification Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use distribution lists</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Create team email lists to notify multiple people with one contact
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Set up email filters</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use email rules to categorize alerts by severity or service
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Monitor credit usage</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Track daily email usage to avoid running out during incidents
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Combine with other channels</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use webhooks or PagerDuty for critical alerts that need immediate attention
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting Email Delivery">
        <DocAlert variant="warning" className="p-6">
          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Not Receiving Emails?
          </h3>
          <ol className={`space-y-2 ${docsColors.text.body}`}>
            <li>
              <strong className={docsColors.text.heading}>1. Check the email address:</strong>{" "}
              Ensure there are no typos
            </li>
            <li>
              <strong className={docsColors.text.heading}>2. Check spam folder:</strong> Look in
              junk/spam for notifications
            </li>
            <li>
              <strong className={docsColors.text.heading}>3. Verify contact assignment:</strong>{" "}
              Confirm the contact is assigned to monitors
            </li>
            <li>
              <strong className={docsColors.text.heading}>4. Check credit balance:</strong> Ensure
              you have available email credits
            </li>
            <li>
              <strong className={docsColors.text.heading}>
                5. Test with a temporary URL change:
              </strong>{" "}
              Point monitor to an invalid URL, wait for down alert, then restore the correct URL
            </li>
          </ol>
          <p className={`text-sm ${docsColors.text.body} mt-4`}>
            If issues persist, contact support at support@uptime-monitor.io
          </p>
        </DocAlert>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications", text: "Notifications" }}
        next={{ href: "/docs/notifications/webhooks", text: "Webhooks" }}
      />
    </DocLayout>
  );
}
